<template>
  <div>
    <h1 class="mb-6 text-2xl text-gray-777 font-roboto uppercase">Prescrição</h1>
    <data-table
      v-if="loadTable"
      v-model:columns="columns"
      :disable-search="true"
      :route="'/patients/prescriptions/' + patient.id"
      no-data-label="Nenhuma prescrição cadastrada"
      no-result-label="Nenhuma prescrição encontrada"
    >
      <template #table.cell.content.actions="{ value, row }">
        <div class="inline-grid grid-cols-1 gap-4 ">
          <a :href="`/patients/prescriptions/download/` + row.id" target="_blank"><font-awesome-icon icon="fa-eye" class="text-green-500 text-lg cursor-pointer" /></a>
        </div>
      </template>
      <template #grid.content.body.actions="{ value, row }">
        <div class="inline-grid grid-cols-1 gap-4 ">
          <a :href="`/patients/prescriptions/download/` + row.id" target="_blank"><font-awesome-icon icon="fa-eye" class="text-green-500 text-lg cursor-pointer" /></a>
        </div>
      </template>

      <template #after.reload-button>
        <button v-if="user.role == 2" class="flex items-end font-roboto font-medium btn-primary uppercase" @click.prevent="openModalEdit({})" :disabled="Array.isArray(this.appointments()) && !this.appointments().length" :title="Array.isArray(this.appointments()) && !this.appointments().length ? 'Nenhum agendamento' : ''">
          <font-awesome-icon icon="fa-plus" class="text-white text-lg mr-0 lg:mr-2" /><span class="hidden lg:block"> Adicionar Prescrição</span>
        </button>
        <div v-if="Array.isArray(this.appointments()) && !this.appointments().length" class="block w-full text-center lg:hidden">Nenhum agendamento</div>
      </template>
    </data-table>
  </div>
  <modal-edit-steps ref="modalEdit" :steps="stepItems" :modal-scrollable="true" :modal-align="this.appointmentOpen() ? 'left' : 'center'" :modal-backdrop="!this.appointmentOpen()">
    <template #content.start_prescription="{item}">
      <select-input v-model="item.appointment_id" :error="item.errors.appointment_id" class="lg:pr-6 w-full lg:w-1/2" label="Atendimento vinculado">
        <option :value="null" disabled class="hidden invisible">Atendimento vinculado</option>
        <option v-for="(value, name, index) in this.appointments()" :key="name" :value="name">{{ this.$filters.formatDateTime(value, 'dd/MM/yyyy - HH:mm') }}</option>
      </select-input>
      <select-input v-model="item.type" :error="item.errors.type" class="w-full mt-6 lg:mt-0 lg:w-1/2" label="Tipo da Prescrição">
        <option :value="null" disabled class="hidden invisible">Tipo da Prescrição</option>
        <option v-for="(value, name, index) in this.prescription_types()" :key="name" :value="name">{{ value }}</option>
      </select-input>
    </template>
    <template #content.generate_prescription="{item}">
      <date-time-input v-model="item.date" :error="item.errors.date" class="lg:pr-6 w-full lg:w-1/2" label="Data de emissão" />
      <switch-input v-model="item.special_control" class="w-full mt-6 lg:mt-0 lg:w-1/2" label="Receita de controle especial" labelChecked="Sim" labelUnchecked="Não" />
      <vue-select-input v-if="this.professional_addresses().length" :searchable="false" v-model="item.address" :options="this.professional_addresses()" :error="item.errors[`address.id`]" class="w-full mt-6" label="Local de atendimento" text="nenhum local de atendimento encontrado">
        <template #option="{ id, name, street, number, postcode, neighborhood, complement, state, city, cnes, phone }">
          {{ name }} - {{ cnes }}
          <div>{{ street }}, {{ number }} - {{ postcode }} - {{ neighborhood }} - {{ city }} - {{ state }}</div>
          <div>{{ phone }}</div>
        </template>
        <template #selected-option="{ id, name }">
          {{ name }}
        </template>
      </vue-select-input>
      <div v-if="!this.professional_addresses().length" class="flex flex-wrap mt-6">
        <div class="w-full mb-2">Local de Atendimento</div>
        <text-input v-model="item.address.name" :error="item.errors[`address.name`]" class="lg:pr-6 w-full lg:w-6/12" placeholder="Nome" />
        <cep-input @updateAddress="(...args) => this.onUpdateAddress(item.address,...args)" v-model="item.address.postcode" :error="item.errors[`address.postcode`]" class="pr-6 w-5/6 lg:w-1/2" placeholder="CEP" />
        <text-input v-model="item.address.street" :error="item.errors[`address.street`]" class="mt-6 lg:pr-6 w-full lg:w-2/3" placeholder="Logradouro" />
        <text-input v-model="item.address.number" :error="item.errors[`address.number`]" class="mt-6 w-full lg:w-1/3" placeholder="Número" />
        <text-input v-model="item.address.complement" :error="item.errors[`address.complement`]" class="mt-6 w-full" placeholder="Complemento" />
        <text-input v-model="item.address.neighborhood" :error="item.errors[`address.neighborhood`]" class="mt-6 lg:pr-6 w-full lg:w-1/3" placeholder="Bairro" />
        <text-input v-model="item.address.city" :error="item.errors[`address.city`]" class="mt-6 lg:pr-6 w-full lg:w-1/3" placeholder="Cidade" />
        <select-input v-model="item.address.state" :error="item.errors[`address.state`]" class="mt-6 w-full lg:w-1/3" placeholder="Estado">
          <option :value="null" disabled class="hidden invisible">Estado</option>
          <option v-for="(value, name, index) in this.states_options()" :key="name" :value="name">{{ value }}</option>
        </select-input>
        <text-input v-model="item.address.cnes" :error="item.errors[`address.cnes`]" class="mt-6 lg:pr-6 w-full lg:w-1/2" placeholder="CNES" />
        <mask-input :mask="['(##) ####-####', '(##) #####-####']" :clear-incomplete="true" v-model="item.address.phone" :error="item.errors[`address.phone`]" class="mt-6 w-full lg:w-1/2" placeholder="Telefone" />
        <switch-input v-model="item.address.save" class="w-full mt-6" labelChecked="Salvar endereço" labelUnchecked="Salvar endereço" />
      </div>
      <textarea-input v-model="item.description" maxlength="1000" :lengthCounter="true" :error="item.errors.description" class="mt-6 w-full" label="Observações" />
      
      <button class="mt-6 flex items-end font-roboto font-medium btn-primary uppercase" @click.prevent="item.medicines.push({name: null});">
        <font-awesome-icon icon="fa-plus" class="text-white text-lg mr-0 lg:mr-2" /> Adicionar Medicamento
      </button>
      <div v-if="item.errors.medicines && !item.medicines.length" class="w-full mt-2 form-error">{{ item.errors.medicines }}</div>
      <div class="w-full flex flex-wrap p-6 mt-4 bg-gray-100/50" v-for="(medicine, medicine_index) in item.medicines">
        <text-input v-model="medicine.name" :error="item.errors[`medicines.`+medicine_index+`.name`]" class="w-5/6 lg:w-11/12" label="Princípio ativo / Medicamento" />
        <div class="mt-6 lg:mt-0 w-1/6 lg:w-1/12 self-center text-right">
          <span @click="deleteMedicine(item, medicine)"><font-awesome-icon icon="fa-xmark" class="text-gray-600 text-lg cursor-pointer" /></span>
        </div>
        <text-input v-model="medicine.concentration" :error="item.errors[`medicines.`+medicine_index+`.concentration`]" class="lg:pr-6 w-full mt-6 lg:w-1/3" label="Concentração" />
        <select-input v-model="medicine.pharmaceutical_form" :error="item.errors[`medicines.`+medicine_index+`.pharmaceutical_form`]" class="lg:pr-6 w-full mt-6 lg:w-1/3" label="Forma farmacêutica">
          <option :value="null" disabled class="hidden invisible">Forma farmacêutica</option>
          <option v-for="(value, name, index) in this.prescription_pharmaceutical_forms()" :key="name" :value="name">{{ value }}</option>
        </select-input>
        <select-input v-model="medicine.route_administration" :error="item.errors[`medicines.`+medicine_index+`.route_administration`]" class="w-full mt-6 lg:w-1/3" label="Via de administração">
          <option :value="null" disabled class="hidden invisible">Via de administração</option>
          <option v-for="(value, name, index) in this.prescription_routes_administration()" :key="name" :value="name">{{ value }}</option>
        </select-input>
        <text-input v-model="medicine.dose" :error="item.errors[`medicines.`+medicine_index+`.dose`]" class="lg:pr-6 w-full mt-6 lg:w-1/2" label="Dose" />
        <switch-input v-model="medicine.single_dose" class="lg:pr-6 w-full mt-6 lg:w-1/4" label="Dose única" labelChecked="Sim" labelUnchecked="Não" />
        <switch-input v-model="medicine.continuous_use" class="w-full mt-6 lg:w-1/4" label="Uso contínuo" labelChecked="Sim" labelUnchecked="Não" />
        <div class="flex flex-wrap w-full mt-6" v-if="medicine.continuous_use">
          <div class="w-full">Posologia</div>
          <select-input v-if="medicine.continuous_use" v-model="medicine.frequency_type" :error="item.errors[`medicines.`+medicine_index+`.frequency_type`]" class="lg:pr-6 w-full mt-6 lg:w-1/3" label="Frequência da dose">
            <option :value="null" disabled class="hidden invisible">Frequência da dose</option>
            <option v-for="(value, name, index) in this.prescription_frequency_types()" :key="name" :value="name">{{ value }}</option>
          </select-input>
          <mask-input mask="###" v-if="medicine.frequency_type == 'interval'" v-model="medicine.interval" :error="item.errors[`medicines.`+medicine_index+`.interval`]" class="w-full mt-6 lg:w-2/3" label="Intervalo" placeholder="horas" />
          <mask-input mask="###" v-if="medicine.frequency_type == 'frequency'" v-model="medicine.frequency" :error="item.errors[`medicines.`+medicine_index+`.frequency`]" class="lg:pr-6 w-full mt-6 lg:w-2/6" label="Frequência" placeholder="vez(es)" />
          <select-input v-if="medicine.frequency_type == 'shift'" v-model="medicine.shift" :error="item.errors[`medicines.`+medicine_index+`.shift`]" class="lg:pr-6 w-full mt-6 lg:w-2/6" label="Turno">
            <option :value="null" disabled class="hidden invisible">Turno</option>
            <option v-for="(value, name, index) in this.prescription_shifts()" :key="name" :value="name">{{ value }}</option>
          </select-input>
          <text-input v-if="medicine.frequency_type == 'frequency' || medicine.frequency_type == 'shift'" v-model="medicine.repeat" :error="item.errors[`medicines.`+medicine_index+`.repeat`]" class="lg:pr-6 w-full mt-6 lg:w-1/6" label="A cada" />
          <select-input v-if="medicine.frequency_type == 'frequency' || medicine.frequency_type == 'shift'" v-model="medicine.repeat_type" :error="item.errors[`medicines.`+medicine_index+`.repeat_type`]" class="w-full mt-6 lg:w-1/6" label="Período">
            <option :value="null" disabled class="hidden invisible">Período</option>
            <option v-for="(value, name, index) in this.prescription_frequencies()" :key="name" :value="name">{{ value }}</option>
          </select-input>
        </div>
        <div class="flex flex-wrap w-full mt-6" v-if="medicine.continuous_use">
          <div class="w-full">Período de tratamento</div>
          <date-input v-model="medicine.date_start" :error="item.errors[`medicines.`+medicine_index+`.date_start`]" class="mt-6 lg:pr-6 w-full lg:w-1/2" label="Data de início" />
          <mask-input mask="###" v-model="medicine.duration" :error="item.errors[`medicines.`+medicine_index+`.duration`]" class="lg:pr-6 w-full mt-6 lg:w-1/4" label="Duração" />
          <select-input v-model="medicine.duration_type" :error="item.errors[`medicines.`+medicine_index+`.duration_type`]" class="w-full mt-6 lg:w-1/4" label="Período">
            <option :value="null" disabled class="hidden invisible">Período</option>
            <option v-for="(value, name, index) in this.prescription_frequencies()" :key="name" :value="name">{{ value }}</option>
          </select-input>
        </div>
      </div>
    </template>
    <template #content.download_prescription="{item}">
      <div class="w-full">
        Faça o download da prescrição e assine o documento digitalmente:
      </div>
      <div class="w-full flex justify-center mt-5">
        <button type="button" class="btn-primary px-6 py-2.5 font-medium text-xs leading-tight uppercase ml-1" @click.prevent="download(item)">Download</button>
      </div>
    </template>
    <template #content.upload_prescription="{item}">
      <div class="w-full">
        Faça o upload do documento assinado digitalmente:
      </div>
      <file-input v-model="item.file" :error="item.errors.file" class="mt-6 w-full" type="file" accept="application/pdf" /> 
    </template>
    <template #content.success>
      <div class="w-full text-center">
        <font-awesome-icon icon="fa-regular fa-circle-check" class="text-green-600 text-7xl mr-0 lg:mr-2" />
      </div>
      <div class="w-full text-center mt-5 uppercase">
        O documento foi enviado para o paciente
      </div>
    </template>
  </modal-edit-steps>
</template>

<script>
import { computed } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'
import format from 'date-fns/format';
import LoadingButton from '@/Shared/Components/LoadingButton'
import ModalEdit from '@/Shared/Modal/ModalEdit'
import ModalEditSteps from '@/Shared/Modal/ModalEditSteps'
import DataTable from '@/Shared/Components/DataTable'
import SwitchInput from '@/Shared/Fields/SwitchInput'
import TextInput from '@/Shared/Fields/TextInput'
import VueSelectInput from '@/Shared/Fields/VueSelectInput'
import DateTimeInput from '@/Shared/Fields/DateTimeInput'
import TextareaInput from '@/Shared/Fields/TextareaInput'
import FileInput from '@/Shared/Fields/FileInput'
import SelectInput from '@/Shared/Fields/SelectInput'
import MaskInput from '@/Shared/Fields/MaskInput'
import DateInput from '@/Shared/Fields/DateInput'
import CepInput from '@/Shared/Fields/CepInput'

export default {
  inject: ['patient', 'appointments', 'professionals', 'professional_addresses', 'prescription_types', 'prescription_pharmaceutical_forms', 'prescription_routes_administration', 'prescription_frequencies', 'prescription_frequency_types', 'prescription_shifts', 'states_options', 'loadOptions', 'appointmentOpen'],
  components: {
    LoadingButton,
    ModalEdit,
    ModalEditSteps,
    DataTable,
    SwitchInput,
    TextInput,
    VueSelectInput,
    DateTimeInput,
    TextareaInput,
    FileInput,
    SelectInput,
    DateInput,
    MaskInput,
    CepInput,
  },
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },
  data() {
    return {
      loadTable: true,
      stepItems: [
        {
          idStep: 'start_prescription',
          title: 'Gerar prescrição',
          submitCallback: (item, callback) => {
            let success = false;
            item.post(`/patients/prescriptions/validate-step-one`, {
                forceFormData: true,
                onFinish: () => {
                  let success = false;
                  if(!Object.keys(item.errors).length) {
                      success = true;
                  }
                  callback(success);
                  if(item.type == 'document') {
                    this.$refs.modalEdit.$refs.steps.selectedItemIndex = 3;
                  }
                }
            });
            return success;
          }
        },
        {
          idStep: 'generate_prescription',
          title: 'Dados da prescrição',
          submitCallback: (item, callback) => {
            let success = false;
            item.post(`/patients/prescriptions/validate-step-two`, {
                forceFormData: true,
                onFinish: () => {
                  let success = false;
                  if(!Object.keys(item.errors).length) {
                      success = true;
                  }
                  callback(success);
                }
            });
            return success;
          }
        },
        {
          idStep: 'download_prescription',
          title: 'Download da prescrição',
        },
        {
          idStep: 'upload_prescription',
          title: 'Envie o documento assinado digitalmente',
          submitCallback: (item, callback) => {
            let success = false;
            item.post(`/patients/prescriptions/` + this.patient.id, {
                forceFormData: true,
                onFinish: () => {
                  let success = false;
                  if(!Object.keys(item.errors).length) {
                      success = true;
                      this.reloadTable();
                      this.loadOptions();
                  }
                  callback(success);
                }
            });
            return success;
          },
          backCallback: (item, callback) => {
            let success = true;
            callback(success);
            if(item.type == 'document') {
              this.$refs.modalEdit.$refs.steps.selectedItemIndex = 0;
            }
            return success;
          }
        },
        {
          idStep: 'success',
          title: 'Prescrição enviada para o paciente',
        },
      ],
      columns: [
        {
          uniqid: 'prescription_date_start',
          label: 'Atendimento',
          field: 'appointments.date_start',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => format(new Date(row.appointment.date_start), 'dd/MM/yyy - HH:mm'),
        },
        {
          uniqid: 'prescription_created_at',
          label: 'Data do documento',
          field: 'created_at',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => format(new Date(row.created_at), 'dd/MM/yyy HH:mm'),
        },
        {
          uniqid: 'actions',
          label: 'Ações',
          field: 'id',
          visible: true,
          align: 'center',
          classes: 'py-3 border-b',
          headerClass: 'p-4',
        },
      ],
    }
  },
  methods: {
    openModalEdit(item) {
      this.$refs.modalEdit.openModalEdit({
        id: item.id,
        appointment_id: item.appointment_id,
        type: item.type,
        description: item.description,
        date: item.date,
        file: item.file,
        medicines: item.medicines ?? [],
        professional: item.professional ?? null,
        address: item.address ?? {},
        special_control: item.special_control ?? false,
      });
    },
    reloadTable() {
      this.loadTable = false;
      setTimeout(() => this.loadTable = true, 10);
    },
    download(item) {
      let data = {
        _token: this.$page.props.csrf_token,
        type_document: 'prescription',
        date: item.date,
        description: item.description,
        medicines: item.medicines,
        special_control: item.special_control ? '1' : '',
        professional_id: item.professional?.id,
        address_id: item.address?.id,
        patient_id: this.patient.id,
        address_name: item.address?.name,
        address_postcode: item.address?.postcode,
        address_street: item.address?.street,
        address_number: item.address?.number,
        address_complement: item.address?.complement,
        address_neighborhood: item.address?.neighborhood,
        address_city: item.address?.city,
        address_state: item.address?.state,
        address_cnes: item.address?.cnes,
        address_phone: item.address?.phone,
      };
      let url = `/patients/pdf/generate`;
      this.openWindowWithPost(url, data);
    },
    openWindowWithPost(url, data) {
      var form = document.createElement("form");
      form.target = "_blank";
      form.method = "POST";
      form.action = url;
      form.style.display = "none";

      for (var key in data) {
        this.generateInput(form, key, data[key]);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    generateInput(form, key, value) {
        if(typeof(value) == 'object' && value != null) {
          for (const [obj_key, obj_value] of Object.entries(value)) {
            let k = key + '|' + obj_key;
            this.generateInput(form, k, obj_value);
          }
        } else {
          let k = key.split('|');
          if(k.length > 1) {
            for(let i in k) {
              if(i == 0) {
                key = k[i];
              } else {
                key += '[' + k[i] + ']';
              }
            }
          }
          var input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = typeof(value) != 'undefined' && value != null ? value : '';
          form.appendChild(input);
        }
    },
    deleteMedicine(form, medicine) {
      form.medicines.splice(form.medicines.indexOf(medicine), 1);
    },
    onUpdateAddress(address, json) {
      address.city = json.localidade;
      address.state = json.uf;
      address.street = json.logradouro;
      address.neighborhood = json.bairro;
    },
  },
}
</script>